/**
 * ChatAppBar — chat-screen top bar with back button, peer avatar + name +
 * identity badge, and conditional action icons (video call, more).
 *
 * Flutter source of truth: `chat_screen.dart:1115-1284`
 *  - `_appBarLeading` (back arrow)
 *  - `_appBarTitle` (avatar + name + IdentityBadge)
 *  - `_appBarActions` (visibility rules + video call / more icons)
 *  - `appBarOptionsButton` (conversation-blocked "more" fallback)
 *
 * Visibility rules (`chat_screen.dart:1184-1263`):
 *  - Blocked (current user blocked OR peer blocked OR peer null) → no actions.
 *  - No mutual subscription → no actions.
 *  - Conversation-blocked (either side) → only "More".
 *  - Otherwise → "Video call" + "More".
 *
 * Tap behavior:
 *  - "More" delegates fully to the page-supplied `onMoreTap` — the host page
 *    owns the chat-actions sheet (`ChatActionsSheet`), so the app bar no longer
 *    opens any placeholder of its own.
 *  - "Video call" delegates fully to the page-supplied `onVideoCallTap` — the
 *    host page owns the caller-initiate confirm sheet + permission-gated launch
 *    (`chat_screen.dart:1212-1234`), so the app bar no longer opens any
 *    placeholder of its own.
 *
 * Deviations from Flutter UI (allowed per `presentation.md`):
 *  - No `AutoSizeText` equivalent on web; single-line ellipsis is used. The
 *    Flutter `minFontSize: 10` shrinking behavior is intentionally dropped.
 *  - Avatar sits to the left of the centered title group (Flutter centers
 *    avatar + name as one block). On wide viewports the Flutter layout leaves
 *    the avatar floating in the middle, which doesn't read well — the web
 *    version keeps avatar + name as a left-anchored, horizontally-centered
 *    unit using `responsive.avatar.xs` for the inline avatar size.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { BackIconButton } from '@/presentation/components/common/BackIconButton';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { IdentityBadge } from '@/presentation/components/common/identity/IdentityBadge';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';
import { UserUtils } from '@/domain/utils/userUtils';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export interface ChatAppBarProps {
  /** The resolved peer user, or `null` until the initial fetch completes. */
  peerUser: UserEntity | null;
  /**
   * True iff the current user is blocked OR the current user is blocked by
   * the peer. Mirrors the Flutter `_currentUserBlocked` predicate used at
   * `chat_screen.dart:1184-1190`. The caller (Task 35 `ChatPage`) collapses
   * both directions of the block check into this single boolean.
   */
  isBlocked: boolean;
  /**
   * True iff either party has locally blocked the conversation. Mirrors the
   * Flutter `_currentUserConversationBlocked || _peerUserConversationBlocked`
   * branch at `chat_screen.dart:1197-1199`.
   */
  isConversationBlocked: boolean;
  /**
   * True iff there is a mutual subscription in either direction. Mirrors
   * Flutter `_subscribedToPeerExclusiveContent || _peerIsSubscribedToExclusiveContent`
   * at `chat_screen.dart:1192-1195`.
   */
  isSubscribedMutual: boolean;
  /** Tap target for the back arrow. */
  onBack: () => void;
  /** Tap target for the avatar / name area (Flutter navigates to user details). */
  onTitleTap: () => void;
  /** Tap target for the "Video call" action icon. */
  onVideoCallTap: () => void;
  /** Tap target for the "More" action icon. */
  onMoreTap: () => void;
  /**
   * Screen key of the hosting page. Retained on the public prop API for the
   * page wiring even though the app bar no longer opens any sheet of its own
   * (the caller-initiate confirm + launch now live in the page handler).
   */
  screenKey?: string;
}

interface ActionIconButtonProps {
  iconPath: string;
  onClick: () => void;
  size: number;
  color: string;
  /** `data-qa-*` set spread onto the action `<button>`. */
  qa?: QaAttrOptions;
}

const ActionIconButton: React.FC<ActionIconButtonProps> = ({
  iconPath,
  onClick,
  size,
  color,
  qa,
}) => (
  <button
    {...(qa ? qaAttr(qa) : {})}
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
    }}
  >
    <AssetIcon path={iconPath} width={size} height={size} color={color} />
  </button>
);

export const ChatAppBar: React.FC<ChatAppBarProps> = ({
  peerUser,
  isBlocked,
  isConversationBlocked,
  isSubscribedMutual,
  onBack,
  onTitleTap,
  onVideoCallTap,
  onMoreTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // --- Visibility rules (`chat_screen.dart:1184-1263`) ---
  // Order matches Flutter:
  //   1. No peer / blocked → no actions
  //   2. No mutual subscription → no actions
  //   3. Conversation-blocked → only "More"
  //   4. Otherwise → "Video call" + "More"
  const peerKnown = peerUser !== null;
  const showAnyActions = peerKnown && !isBlocked && isSubscribedMutual;
  const showVideoCall = showAnyActions && !isConversationBlocked;
  const showMore = showAnyActions;

  // "Video call" — delegate fully to the page-supplied callback, which shows
  // the caller-initiate confirm sheet and runs the permission-gated launch
  // (`chat_screen.dart:1212-1234`). The app bar no longer owns any placeholder
  // for this action.
  const handleVideoCall = (): void => {
    onVideoCallTap();
  };
  // "More" — delegate fully to the page-supplied callback, which opens the
  // real chat-actions sheet (`ChatActionsSheet`). The app bar no longer owns
  // any placeholder for this action.
  const handleMore = (): void => {
    onMoreTap();
  };

  const peerName = peerUser?.name ?? '-';
  const peerFirebaseId = peerUser?.firebaseId ?? null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: responsive.topBar.height,
        paddingLeft: responsive.spacing.sm,
        paddingRight: responsive.spacing.sm,
        gap: responsive.spacing.sm,
        backgroundColor: theme.background,
        boxSizing: 'border-box',
      }}
    >
      {/* Leading: back arrow (`_appBarLeading`, `chat_screen.dart:1126-1133`). */}
      <BackIconButton onClick={onBack} qa={{ id: 'chat-back' }} />

      {/*
       * Title group: avatar + name + identity badge (`_appBarTitle`,
       * `chat_screen.dart:1135-1182`).
       *
       * Web-side deviation from Flutter: the avatar sits OUTSIDE the title
       * button (and to the left of the name) so that the avatar + name +
       * badge group reads as a single left-anchored unit on wide viewports
       * — rather than the Flutter-style centered title with the avatar
       * floating in the middle. Both the avatar and the title button stay
       * tappable and share `onTitleTap` (the avatar gets its own wrapper
       * button so keyboard/screen-reader users can also activate it).
       */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: responsive.spacing.sm,
        }}
      >
        <button
          {...qaAttr({ id: `chat-peer-${peerFirebaseId ?? 'unknown'}` })}
          onClick={onTitleTap}
          aria-label={peerName}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            flexShrink: 0,
          }}
        >
          <CachedImage
            src={
              peerFirebaseId !== null
                ? UserUtils.generateUserProfileImageUrl(peerFirebaseId)
                : ''
            }
            alt={peerName}
            style={{
              width: responsive.avatar.xs,
              height: responsive.avatar.xs,
              borderRadius: responsive.borderRadius.round,
              border: `${responsive.border.thin}px solid ${theme.exclusive}`,
              objectFit: 'cover',
            }}
          />
        </button>
        <button
          {...qaAttr({ id: `chat-peer-name-${peerFirebaseId ?? 'unknown'}` })}
          onClick={onTitleTap}
          style={{
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: responsive.spacing.sm,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <span
            style={{
              minWidth: 0,
              fontSize: responsive.font.lg,
              fontWeight: responsive.fontWeight.bold,
              color: theme.text.primary,
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {peerName}
          </span>
          {peerFirebaseId !== null && <IdentityBadge userId={peerFirebaseId} />}
        </button>
      </div>

      {/* Actions: video call + more (`_appBarActions`, `chat_screen.dart:1184-1263`). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: responsive.gap.base,
          flexShrink: 0,
        }}
      >
        {showVideoCall && (
          <ActionIconButton
            iconPath="/assets/images/video_cam.png"
            onClick={handleVideoCall}
            size={responsive.icon.large}
            color={theme.whiteLight}
            qa={{ id: 'chat-video-call' }}
          />
        )}
        {showMore && (
          <ActionIconButton
            iconPath="/assets/images/more_filled.png"
            onClick={handleMore}
            size={responsive.icon.large}
            color={theme.whiteLight}
            qa={{ id: 'chat-actions' }}
          />
        )}
      </div>
    </div>
  );
};
